import React, { ChangeEvent, ReactElement, MouseEvent, useEffect, useRef, useState } from 'react';
import {
  ClinicSettingsConversationsChannelStatusFragment as Status,
  ClinicSettingsConversationsChannelStatusChannelAssigneeFragment,
  useDeleteClinicSettingsConversationsChannelStatusChannelAssigneeMutation,
  useCreateClinicSettingsConversationsChannelStatusChannelAssigneeMutation,
  ChannelStatusAction,
  PermissionType,
  ChannelStatusUpdateInput,
} from 'shared/types/graphql';
import {
  AvatarGroupSelect,
  IAvatarGroupSelectOption,
  useAvatarGroupSelectOptions,
} from 'shared/components/Avatars/AvatarGroupSelect';
import { PopoverPlacement } from 'shared/components/Dropdown';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { DragHandle } from '@televet/kibble-ui/build/components/SortableList';
import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { channelStatusActionLabels } from 'shared/components/ConversationStatusSelect';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import ChannelStatusColorPickerWrapper from 'pages/Settings/pages/ConversationSettings/ChannelStatusColorPickerWrapper';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

interface Props {
  handleChannelStatusUpdate: (channelStatusId: string, data: ChannelStatusUpdateInput) => void;
  id: string;
  status: Status;
  index: number | undefined;
  filter: string;
  lastAdded: string;
  onDelete: () => void;
  setLastAdded: (lastAdded: string) => void;
  statusesLength: number;
  statusType: string;
}

const StatusElement = ({
  id,
  status,
  handleChannelStatusUpdate,
  onDelete,
  index = 0,
  filter,
  lastAdded,
  setLastAdded,
  statusesLength,
  statusType,
}: Props): ReactElement => {
  const { isExportToPimsSupported } = useIntegrationsProvider();
  const { currentClinicId, currentClinic, userHasPermission } = useClinicUser();
  const hasPermission = userHasPermission(PermissionType.WriteClinic);
  const { trackEvent } = useTracking();

  const [exportStatus, setExportStatus] = useState(!!status?.isExportedToPims);
  const toast = useToast();
  const integrations = currentClinic?.integrations;
  const focusRef = useRef<HTMLInputElement>(null);

  const [createChannelStatusChannelAssignee] = useCreateClinicSettingsConversationsChannelStatusChannelAssigneeMutation(
    {
      refetchQueries: ['getClinicSettingsConversationsChannelStatuses'],
    },
  );

  const [deleteChannelStatusChannelAssignee] = useDeleteClinicSettingsConversationsChannelStatusChannelAssigneeMutation(
    {
      refetchQueries: ['getClinicSettingsConversationsChannelStatuses'],
    },
  );

  const assignedToOptions = useAvatarGroupSelectOptions({
    selectedUserIds: (status?.channelStatusChannelAssignees || []).map(
      ({ user: { id } }: ClinicSettingsConversationsChannelStatusChannelAssigneeFragment) => id,
    ),
  });

  useEffect(() => {
    if (lastAdded === id) {
      if (focusRef && focusRef?.current) {
        focusRef?.current?.focus();
        setLastAdded('');
      }
    }
  }, [filter, id, lastAdded, setLastAdded]);

  const handleStatusNameChange = (e: ChangeEvent<HTMLInputElement>, statusId: string): void => {
    const name = e.currentTarget.value;
    if (name) {
      handleChannelStatusUpdate(statusId, { name });
      if (status.name !== name) {
        trackEvent(SettingsConversationEvents.EditStatusName, { StatusType: statusType });
      }
    }
  };

  const handleAssigneeToggle = (e: MouseEvent, option: IAvatarGroupSelectOption, channelStatusId: string): void => {
    if (option.isSelected) {
      const channelStatusChannelAssignee = (status?.channelStatusChannelAssignees || []).find(
        (cs) => cs.user.id === option.value.id,
      );
      deleteChannelStatusChannelAssignee({
        variables: {
          id: channelStatusChannelAssignee?.id,
        },
      });
      toast({ title: 'Assignee Removed', status: 'success' });
    } else {
      createChannelStatusChannelAssignee({
        variables: {
          data: {
            clinic: { connect: { id: currentClinicId } },
            user: { connect: { id: option.value.id } },
            channelStatus: { connect: { id: channelStatusId } },
          },
        },
      });
      toast({ title: 'Assignee Added', status: 'success' });
    }
  };

  const handleToggle = (): void => {
    setExportStatus(!exportStatus);
    const isExportedToPims = !exportStatus;
    handleChannelStatusUpdate(status.id, { isExportedToPims });
  };

  const handleDelete = (): void => {
    onDelete();
    trackEvent(SettingsConversationEvents.DeleteStatus, { StatusType: statusType });
    toast({ title: 'Status Deleted', status: 'success' });
  };

  return (
    <Flex
      align="center"
      gap={2}
      key={`status-${status.id}-${index}`}
      padding={2}
      bg={(index && index % 2 === 0) || index === 0 ? 'background.default' : 'transparent'}
    >
      <Box display="inline-block" h={8} w={8}>
        {hasPermission && <DragHandle />}
      </Box>

      <Flex flex="1">
        <TextInput
          ref={index === statusesLength - 1 && id === lastAdded ? focusRef : null}
          placeholder={status.name}
          px={0}
          onBlur={(e): void => {
            handleStatusNameChange(e, status.id);
          }}
          maxLength={40}
          isDisabled={!hasPermission}
          size="sm"
          border="none"
          bgColor="transparent"
          _placeholder={{ color: 'text.default' }}
          _disabled={{ cursor: 'not-allowed' }}
          _focus={{
            _placeholder: { color: 'text.placeholder' },
          }}
        />
      </Flex>

      <ChannelStatusColorPickerWrapper
        hasPermission={hasPermission}
        currentStatusColor={status.color}
        channelStatusId={status.id}
        handleChannelStatusUpdate={handleChannelStatusUpdate}
      />
      <Flex flex="1">
        <AvatarGroupSelect
          hasPermission={hasPermission}
          options={assignedToOptions}
          popoverPlacement={PopoverPlacement.BottomEnd}
          addButtonTooltip="Assign team member"
          onToggle={(e: MouseEvent, option: IAvatarGroupSelectOption): void =>
            handleAssigneeToggle(e, option, status.id)
          }
          showRemoveButton={true}
        />
      </Flex>
      {integrations?.length ? (
        isExportToPimsSupported ? (
          <Flex align="center" grow={1}>
            <Checkbox isDisabled={!hasPermission} isChecked={exportStatus} onChange={handleToggle} />
          </Flex>
        ) : null
      ) : null}

      <Box display="inline-block" h={10} w={10}>
        {(statusesLength > 1 || status.name === channelStatusActionLabels[ChannelStatusAction.Inactive]) && // Requires at least one status except for inactive
          hasPermission && <Button iconName="trashcan" variant="ghostNeutral" onClick={handleDelete} />}
      </Box>
    </Flex>
  );
};

export default StatusElement;
